import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Typography,
  CircularProgress,
  LinearProgress,
  Pagination,
  Button,
  useTheme,
  IconButton,
  Drawer,
  Divider,
  List,
  ListItem,
  ListItemText,
  Tooltip
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  ContentCopy as CopyIcon,
  AttachMoney as RevenueIcon,
  Analytics as AnalyticsIcon,
  CreditCard as CardIcon,
  Close as CloseIcon,
  Receipt as ReceiptIcon,
  Code as CodeIcon,
  ShoppingBag as CatalogIcon
} from '@mui/icons-material';
import { dashboardService } from '../../../services/dashboard-service';
import { toast } from 'react-toastify';

// High-fidelity fallback mock data matching backend DTO definitions
const mockMonetizationStats = {
  totalRevenue: 5420.00,
  averageOrderValue: 74.25,
  successRate: 94.8,
  activeProductsCount: 4,
  revenueTrendSparkline: [620.00, 480.00, 750.00, 920.00, 540.00, 890.00, 1220.00],
  providerBreakdown: { "STRIPE": 48, "APPLE": 22, "GOOGLE": 14 },
  statusBreakdown: { "SUCCESS": 73, "FAILED": 4, "PENDING": 7 }
};

const mockPurchaseOrders = {
  totalCount: 7,
  orders: [
    { orderId: "ord_rev_7718a", accountId: "acc_premium_01", productId: "classroom_bundle_yearly", provider: "STRIPE", purchaseToken: "tok_stripe_sec_9918", purchaseDate: "2026-05-25T14:30:00Z", status: "SUCCESS", environment: "PRODUCTION", payload: '{"stripe_charge_id": "ch_3M4r9yLkd","billing_details": {"email": "johndoe@school.edu","country": "US"},"card": {"brand": "visa","last4": "4242"},"risk_score": 12}', createdAt: "2026-05-25T14:30:00Z" },
    { orderId: "ord_rev_1029b", accountId: "acc_trial_02", productId: "classroom_bundle_monthly", provider: "APPLE", purchaseToken: "tok_apns_iap_2291", purchaseDate: "2026-05-24T18:15:00Z", status: "SUCCESS", environment: "PRODUCTION", payload: '{"apple_transaction_id": "4900010992","in_app_ownership_type": "PURCHASED","quantity": 1,"device_ip": "172.56.21.9"}', createdAt: "2026-05-24T18:15:00Z" },
    { orderId: "ord_rev_4432c", accountId: "acc_premium_04", productId: "classroom_bundle_yearly", provider: "GOOGLE", purchaseToken: "tok_play_iap_5539", purchaseDate: "2026-05-23T09:40:00Z", status: "SUCCESS", environment: "PRODUCTION", payload: '{"google_order_id": "GPA.3312-8819-2311","purchase_state": 0,"consumption_state": 1,"signature": "sig_google_play_82"}', createdAt: "2026-05-23T09:40:00Z" },
    { orderId: "ord_rev_9921d", accountId: "acc_premium_04", productId: "classroom_bundle_monthly", provider: "STRIPE", purchaseToken: "tok_stripe_sec_1188", purchaseDate: "2026-05-22T11:20:00Z", status: "FAILED", environment: "PRODUCTION", payload: '{"stripe_error_code": "card_declined","decline_code": "insufficient_funds","card": {"brand": "mastercard","last4": "1002"}}', createdAt: "2026-05-22T11:20:00Z" },
    { orderId: "ord_rev_8271e", accountId: "acc_school_05", productId: "school_enterprise_yearly", provider: "STRIPE", purchaseToken: "tok_stripe_sec_4455", purchaseDate: "2026-05-20T16:00:00Z", status: "SUCCESS", environment: "PRODUCTION", payload: '{"stripe_charge_id": "ch_99182aKds","billing_details": {"email": "charlie@school.edu","name": "Charlie Chaplin"},"card": {"brand": "amex","last4": "3007"}}', createdAt: "2026-05-20T16:00:00Z" },
    { orderId: "ord_rev_3012f", accountId: "acc_premium_01", productId: "classroom_bundle_monthly", provider: "STRIPE", purchaseToken: "tok_stripe_sec_0029", purchaseDate: "2026-05-18T10:10:00Z", status: "PENDING", environment: "SANDBOX", payload: '{"stripe_source_status": "chargeable","charge_pending": true}', createdAt: "2026-05-18T10:10:00Z" },
    { orderId: "ord_rev_8829g", accountId: "acc_trial_02", productId: "classroom_bundle_monthly", provider: "APPLE", purchaseToken: "tok_apns_iap_7718", purchaseDate: "2026-05-16T12:00:00Z", status: "CANCELLED", environment: "SANDBOX", payload: '{"apple_transaction_id": "1900021021","cancellation_reason": "USER_REQUEST","refund_status": 1}', createdAt: "2026-05-16T12:00:00Z" }
  ]
};

const mockProducts = [
  { productId: "classroom_bundle_monthly", offeringType: "CLASSROOM", offeringId: "off_classroom_m", name: "Classroom Monthly Bundle", description: "Standard classroom bundle allowing up to 30 readers, auto-renewing monthly.", storeType: "STRIPE", storeListingId: "price_stripe_classroom_m", status: "ACTIVE" },
  { productId: "classroom_bundle_yearly", offeringType: "CLASSROOM", offeringId: "off_classroom_y", name: "Classroom Yearly Bundle", description: "Discounted yearly bundle allowing up to 30 readers, auto-renewing annually.", storeType: "STRIPE", storeListingId: "price_stripe_classroom_y", status: "ACTIVE" },
  { productId: "school_enterprise_yearly", offeringType: "ENTERPRISE", offeringId: "off_enterprise_y", name: "School Enterprise License", description: "Enterprise scaling license for entire school systems with custom quotas.", storeType: "STRIPE", storeListingId: "price_stripe_enterprise_y", status: "ACTIVE" },
  { productId: "individual_monthly", offeringType: "INDIVIDUAL", offeringId: "off_individual_m", name: "Individual Monthly Plan", description: "Standard monthly plan for parents allowing 1 student profile.", storeType: "APPLE", storeListingId: "sku_apple_individual_m", status: "ACTIVE" }
];

const MonetizationDashboardTab = () => {
  const theme = useTheme();

  // Telemetry states
  const [statsLoading, setStatsLoading] = useState(true);
  const [stats, setStats] = useState({
    totalRevenue: 0.0,
    averageOrderValue: 0.0,
    successRate: 100.0,
    activeProductsCount: 0,
    revenueTrendSparkline: [0, 0, 0, 0, 0, 0, 0],
    providerBreakdown: {},
    statusBreakdown: {}
  });

  // Table transaction states
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [totalCount, setTotalCount] = useState(0);

  // Products state
  const [productsLoading, setProductsLoading] = useState(true);
  const [products, setProducts] = useState([]);

  // Mock checking indicator
  const [isMocked, setIsMocked] = useState(false);

  // Filters state
  const [term, setTerm] = useState('');
  const [status, setStatus] = useState('ALL');
  const [provider, setProvider] = useState('ALL');
  const [page, setPage] = useState(1);
  const size = 5; // Page size

  // Selected order details drawer
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [payloadOpen, setPayloadOpen] = useState(false);
  const [receiptLoading, setReceiptLoading] = useState(false);
  const [ownerDetails, setOwnerDetails] = useState(null);
  const [ownerLoading, setOwnerLoading] = useState(false);

  // Helper to resolve product prices inside mock fallback
  const getProductPrice = (productId) => {
    const sku = productId.toLowerCase();
    if (sku.includes("enterprise") || sku.includes("school")) return 1200.00;
    if (sku.includes("bundle") || sku.includes("classroom")) return 120.00;
    if (sku.includes("yearly") || sku.includes("annual")) return 99.00;
    if (sku.includes("monthly")) return 14.99;
    if (sku.includes("trial")) return 0.00;
    return 19.99;
  };

  const handleOrderClick = async (orderRow) => {
    try {
      setSelectedOrder(orderRow);
      setReceiptLoading(true);
      setPayloadOpen(false);

      const receipt = await dashboardService.getOrderReceipt(orderRow.orderId);
      setSelectedOrder(prev => {
        if (!prev || prev.orderId !== orderRow.orderId) return prev;
        return {
          ...prev,
          purchaseReceiptId: receipt.purchaseReceiptId,
          amount: receipt.amount,
          isVerified: receipt.isVerified,
          formattedBillingAddress: receipt.formattedBillingAddress,
          payload: receipt.payload
        };
      });
    } catch (err) {
      console.warn("Using offline mock details fallback for order receipt:", err.message);
      setSelectedOrder(prev => {
        if (!prev || prev.orderId !== orderRow.orderId) return prev;
        return {
          ...prev,
          purchaseReceiptId: `rcpt_${prev.orderId.split('_')[2] || 'gen_9918'}`,
          amount: getProductPrice(prev.productId),
          isVerified: prev.status === 'SUCCESS',
          formattedBillingAddress: prev.provider === 'STRIPE' ? 'United States' : null
        };
      });
    } finally {
      setReceiptLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      setStatsLoading(true);
      const res = await dashboardService.getMonetizationStats();
      setStats(res);
      setIsMocked(false);
    } catch (err) {
      console.warn("Using offline mock data fallback for monetization stats:", err.message);
      setStats(mockMonetizationStats);
      setIsMocked(true);
    } finally {
      setStatsLoading(false);
    }
  };

  const fetchOrders = async () => {
    try {
      setOrdersLoading(true);
      const query = {
        term: term.trim() ? term.trim() : null,
        status: status === 'ALL' ? null : status,
        provider: provider === 'ALL' ? null : provider,
        page: page - 1,
        size: size
      };
      const res = await dashboardService.searchPurchaseOrders(query);
      setOrders(res.orders || []);
      setTotalCount(res.totalCount || 0);
    } catch (err) {
      console.warn("Using offline mock data fallback for orders search:", err.message);
      let filtered = [...mockPurchaseOrders.orders];

      if (term.trim()) {
        const queryTerm = term.trim().toLowerCase();
        filtered = filtered.filter(o => 
          o.orderId.toLowerCase().includes(queryTerm) ||
          o.accountId.toLowerCase().includes(queryTerm) ||
          o.productId.toLowerCase().includes(queryTerm) ||
          o.purchaseToken.toLowerCase().includes(queryTerm)
        );
      }

      if (status !== 'ALL') {
        filtered = filtered.filter(o => o.status === status);
      }

      if (provider !== 'ALL') {
        filtered = filtered.filter(o => o.provider === provider);
      }

      const total = filtered.length;
      const start = (page - 1) * size;
      const paged = filtered.slice(start, start + size);

      setOrders(paged);
      setTotalCount(total);
    } finally {
      setOrdersLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      setProductsLoading(true);
      const res = await dashboardService.getMonetizationProducts();
      setProducts(res || []);
    } catch (err) {
      console.warn("Using offline mock data fallback for products list:", err.message);
      setProducts(mockProducts);
    } finally {
      setProductsLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
    fetchProducts();
  }, []);

  useEffect(() => {
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, status, provider]);

  // Load owner details when an order is selected
  useEffect(() => {
    const fetchOwnerDetails = async () => {
      if (!selectedOrder || !selectedOrder.accountId) {
        setOwnerDetails(null);
        return;
      }
      try {
        setOwnerLoading(true);
        setOwnerDetails(null);

        // 1. Search account by selectedOrder.accountId
        const accountResult = await dashboardService.searchAccounts({
          term: selectedOrder.accountId,
          subscriptionStatuses: [],
          subscriptionTypes: [],
          pageNumber: 0,
          pageSize: 1
        });

        let ownerId = null;
        if (accountResult && accountResult.items && accountResult.items.length > 0) {
          const matchedAcc = accountResult.items.find(a => a.id === selectedOrder.accountId) || accountResult.items[0];
          ownerId = matchedAcc.ownerId;
        }

        // Fallback ownerId mapping if not returned
        if (!ownerId) {
          const mockOwnerMap = {
            "acc_premium_01": "usr_9281a",
            "acc_trial_02": "usr_3847b",
            "acc_premium_04": "usr_5561f",
            "acc_school_05": "usr_7726e"
          };
          ownerId = mockOwnerMap[selectedOrder.accountId];
        }

        if (ownerId) {
          // 2. Fetch user details by ownerId
          const userResult = await dashboardService.searchUsers({
            term: ownerId,
            role: null,
            status: null,
            page: 0,
            size: 1
          });

          if (userResult && userResult.users && userResult.users.length > 0) {
            const matchedUser = userResult.users.find(u => u.userId === ownerId) || userResult.users[0];
            setOwnerDetails({
              name: matchedUser.name,
              email: matchedUser.email,
              isMocked: false
            });
            return;
          }
        }

        // Fallback local mock lookup
        const localMockOwners = {
          "acc_premium_01": { name: "John Doe", email: "johndoe@school.edu" },
          "acc_trial_02": { name: "Alice Cooper", email: "reader_alice@school.edu" },
          "acc_premium_04": { name: "Eve Online", email: "reader_eve@school.edu" },
          "acc_school_05": { name: "Charlie Chaplin", email: "charlie@school.edu" }
        };
        
        let fallback = localMockOwners[selectedOrder.accountId];
        if (!fallback) {
          const shortId = selectedOrder.accountId.replace("acc_", "").substring(0, 8);
          fallback = {
            name: `Owner (${shortId})`,
            email: `owner.${shortId}@school.edu`
          };
        }

        setOwnerDetails({
          name: fallback.name,
          email: fallback.email,
          isMocked: true
        });
      } catch (err) {
        console.warn("Could not load real account owner details for order:", err.message);
        // Fallback local mock lookup
        const localMockOwners = {
          "acc_premium_01": { name: "John Doe", email: "johndoe@school.edu" },
          "acc_trial_02": { name: "Alice Cooper", email: "reader_alice@school.edu" },
          "acc_premium_04": { name: "Eve Online", email: "reader_eve@school.edu" },
          "acc_school_05": { name: "Charlie Chaplin", email: "charlie@school.edu" }
        };
        
        let fallback = localMockOwners[selectedOrder.accountId];
        if (!fallback) {
          const shortId = selectedOrder.accountId.replace("acc_", "").substring(0, 8);
          fallback = {
            name: `Owner (${shortId})`,
            email: `owner.${shortId}@school.edu`
          };
        }

        setOwnerDetails({
          name: fallback.name,
          email: fallback.email,
          isMocked: true
        });
      } finally {
        setOwnerLoading(false);
      }
    };
    fetchOwnerDetails();
  }, [selectedOrder]);

  const handleFilterSubmit = (e) => {
    e.preventDefault();
    setPage(1);
    fetchOrders();
  };

  const copyToClipboard = (text, typeName = "ID") => {
    navigator.clipboard.writeText(text);
    toast.info(`Copied ${typeName} to clipboard: ${text}`);
  };

  const formatDate = (isoString) => {
    if (!isoString) return '-';
    return new Date(isoString).toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const getStatusChipColor = (stateStr) => {
    switch (stateStr) {
      case 'SUCCESS':
        return {
          backgroundColor: 'rgba(76, 175, 80, 0.15)',
          color: theme.palette.success.main,
          borderColor: 'rgba(76, 175, 80, 0.3)'
        };
      case 'PENDING':
        return {
          backgroundColor: 'rgba(255, 152, 0, 0.15)',
          color: theme.palette.warning.main,
          borderColor: 'rgba(255, 152, 0, 0.3)'
        };
      case 'FAILED':
      case 'CANCELLED':
        return {
          backgroundColor: 'rgba(244, 67, 54, 0.15)',
          color: theme.palette.error.main,
          borderColor: 'rgba(244, 67, 54, 0.3)'
        };
      default:
        return {
          backgroundColor: theme.palette.surfaceContainerLow,
          color: theme.palette.text.secondary,
          borderColor: theme.palette.outlineVariant
        };
    }
  };

  const getProviderChipColor = (provStr) => {
    switch (provStr) {
      case 'STRIPE':
        return { backgroundColor: '#e8f0fe', color: '#1a73e8' };
      case 'APPLE':
        return { backgroundColor: '#f1f1f1', color: '#111111' };
      case 'GOOGLE':
        return { backgroundColor: '#e6f4ea', color: '#137333' };
      default:
        return {};
    }
  };

  // Sparkline SVG path plotter
  const renderSparkline = (sparklineData) => {
    if (!sparklineData || sparklineData.length === 0) return null;
    const maxVal = Math.max(1, ...sparklineData);
    const minVal = Math.min(...sparklineData);
    const range = maxVal - minVal || 1;

    const width = 300;
    const height = 60;
    const padding = 5;

    const points = sparklineData.map((val, idx) => {
      const x = padding + (idx * (width - padding * 2)) / (sparklineData.length - 1);
      const y = height - padding - ((val - minVal) * (height - padding * 2)) / range;
      return `${x},${y}`;
    }).join(' ');

    return (
      <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} style={{ overflow: 'visible' }}>
        <polyline
          fill="none"
          stroke={theme.palette.primary.main}
          strokeWidth="4"
          strokeLinecap="round"
          strokeLinejoin="round"
          points={points}
        />
      </svg>
    );
  };

  // Calculate totals and distributions for visuals
  const stripeCount = stats.providerBreakdown["STRIPE"] || 0;
  const appleCount = stats.providerBreakdown["APPLE"] || 0;
  const googleCount = stats.providerBreakdown["GOOGLE"] || 0;
  const totalProviders = stripeCount + appleCount + googleCount || 1;

  const stripePercent = (stripeCount / totalProviders) * 100;
  const applePercent = (appleCount / totalProviders) * 100;
  const googlePercent = (googleCount / totalProviders) * 100;

  return (
    <Box sx={{ animation: 'fadeIn 0.5s ease-out' }}>

      {isMocked && (
        <Box sx={{
          mb: 3,
          p: 2,
          borderRadius: '12px',
          backgroundColor: 'rgba(244, 67, 54, 0.1)',
          border: `1px solid ${theme.palette.error.main}`,
          color: theme.palette.error.main,
          fontWeight: 700,
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          ⚠️ Demo Mode: Backend API offline. Displaying explicitly mocked monetization records.
        </Box>
      )}

      {/* KPI Stats Row */}
      <Grid container spacing={3} sx={{ mb: 4 }}>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: '16px' }}>
            <CardContent sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 2.5 }}>
              <Box sx={{
                p: 1.5,
                borderRadius: '12px',
                backgroundColor: 'rgba(76, 175, 80, 0.1)',
                color: theme.palette.success.main
              }}>
                <RevenueIcon fontSize="medium" />
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 700, textTransform: 'uppercase' }}>
                  Total Platform Revenue
                </Typography>
                <Typography variant="h3" sx={{ fontWeight: 700, mt: 0.5 }}>
                  {statsLoading ? <CircularProgress size={20} /> : formatCurrency(stats.totalRevenue)}{isMocked && <span style={{ fontSize: '13px', color: theme.palette.error.main, marginLeft: '6px' }}>(Mocked)</span>}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: '16px' }}>
            <CardContent sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 2.5 }}>
              <Box sx={{
                p: 1.5,
                borderRadius: '12px',
                backgroundColor: 'rgba(26, 115, 232, 0.1)',
                color: '#1a73e8'
              }}>
                <AnalyticsIcon fontSize="medium" />
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 700, textTransform: 'uppercase' }}>
                  Average Order Value (AOV)
                </Typography>
                <Typography variant="h3" sx={{ fontWeight: 700, mt: 0.5 }}>
                  {statsLoading ? <CircularProgress size={20} /> : formatCurrency(stats.averageOrderValue)}{isMocked && <span style={{ fontSize: '13px', color: theme.palette.error.main, marginLeft: '6px' }}>(Mocked)</span>}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: '16px' }}>
            <CardContent sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 2.5 }}>
              <Box sx={{
                p: 1.5,
                borderRadius: '12px',
                backgroundColor: 'rgba(255, 152, 0, 0.1)',
                color: theme.palette.warning.main
              }}>
                <CardIcon fontSize="medium" />
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 700, textTransform: 'uppercase' }}>
                  Payment Success Rate
                </Typography>
                <Typography variant="h3" sx={{ fontWeight: 700, mt: 0.5 }}>
                  {statsLoading ? <CircularProgress size={20} /> : `${stats.successRate.toFixed(1)}%`}{isMocked && <span style={{ fontSize: '13px', color: theme.palette.error.main, marginLeft: '6px' }}>(Mocked)</span>}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: '16px' }}>
            <CardContent sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 2.5 }}>
              <Box sx={{
                p: 1.5,
                borderRadius: '12px',
                backgroundColor: 'rgba(156, 39, 176, 0.1)',
                color: '#9c27b0'
              }}>
                <CatalogIcon fontSize="medium" />
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 700, textTransform: 'uppercase' }}>
                  Active Products
                </Typography>
                <Typography variant="h3" sx={{ fontWeight: 700, mt: 0.5 }}>
                  {statsLoading ? <CircularProgress size={20} /> : stats.activeProductsCount.toLocaleString()}{isMocked && <span style={{ fontSize: '13px', color: theme.palette.error.main, marginLeft: '6px' }}>(Mocked)</span>}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

      </Grid>

      {/* Visual Analytics Row */}
      <Grid container spacing={3} sx={{ mb: 4 }}>

        <Grid item xs={12} md={7}>
          <Card sx={{ borderRadius: '16px', height: '100%' }}>
            <CardContent sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 3 }}>
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 700, textTransform: 'uppercase', display: 'block', mb: 2 }}>
                  7-Day Financial Performance
                </Typography>
                <Box sx={{ mt: 1 }}>
                  {statsLoading ? <CircularProgress size={20} /> : renderSparkline(stats.revenueTrendSparkline)}
                </Box>
              </Box>
              <Box sx={{ textAlign: 'right', minWidth: 100 }}>
                <Typography variant="h5" sx={{ fontWeight: 800, color: theme.palette.primary.main }}>
                  7-Day Trend
                </Typography>
                <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                  Completed Revenue
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={5}>
          <Card sx={{ borderRadius: '16px', height: '100%' }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 700, textTransform: 'uppercase', display: 'block', mb: 2 }}>
                Payment Provider Distribution
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>Stripe</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>{stripeCount} ({stripePercent.toFixed(0)}%)</Typography>
                  </Box>
                  <LinearProgress variant="determinate" value={stripePercent} sx={{ height: 6, borderRadius: 3, backgroundColor: 'rgba(26,115,232,0.1)', '& .MuiLinearProgress-bar': { backgroundColor: '#1a73e8' } }} />
                </Box>

                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>Apple App Store</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>{appleCount} ({applePercent.toFixed(0)}%)</Typography>
                  </Box>
                  <LinearProgress variant="determinate" value={applePercent} sx={{ height: 6, borderRadius: 3, backgroundColor: 'rgba(17,17,17,0.1)', '& .MuiLinearProgress-bar': { backgroundColor: '#111111' } }} />
                </Box>

                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>Google Play Store</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>{googleCount} ({googlePercent.toFixed(0)}%)</Typography>
                  </Box>
                  <LinearProgress variant="determinate" value={googlePercent} sx={{ height: 6, borderRadius: 3, backgroundColor: 'rgba(19,115,51,0.1)', '& .MuiLinearProgress-bar': { backgroundColor: '#137333' } }} />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

      </Grid>

      {/* Datatable Filter Toolbar Card */}
      <Card sx={{ mb: 4, borderRadius: '16px' }}>
        <CardContent sx={{ p: 3 }}>
          <form onSubmit={handleFilterSubmit}>
            <Grid container spacing={3} alignItems="center">

              <Grid item xs={12} sm={4} md={5}>
                <TextField
                  fullWidth
                  label="Search transactions"
                  placeholder="Search by Order ID, Account ID, or Product ID..."
                  value={term}
                  onChange={(e) => setTerm(e.target.value)}
                  InputProps={{
                    startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />
                  }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                />
              </Grid>

              <Grid item xs={12} sm={3} md={2.5}>
                <FormControl fullWidth>
                  <InputLabel id="monetization-status-select-label">Payment Status</InputLabel>
                  <Select
                    labelId="monetization-status-select-label"
                    value={status}
                    label="Payment Status"
                    onChange={(e) => { setStatus(e.target.value); setPage(1); }}
                    sx={{ borderRadius: '12px' }}
                  >
                    <MenuItem value="ALL">All Statuses</MenuItem>
                    <MenuItem value="SUCCESS">Success</MenuItem>
                    <MenuItem value="PENDING">Pending</MenuItem>
                    <MenuItem value="FAILED">Failed</MenuItem>
                    <MenuItem value="CANCELLED">Cancelled</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={3} md={2.5}>
                <FormControl fullWidth>
                  <InputLabel id="monetization-provider-select-label">Provider</InputLabel>
                  <Select
                    labelId="monetization-provider-select-label"
                    value={provider}
                    label="Provider"
                    onChange={(e) => { setProvider(e.target.value); setPage(1); }}
                    sx={{ borderRadius: '12px' }}
                  >
                    <MenuItem value="ALL">All Providers</MenuItem>
                    <MenuItem value="STRIPE">Stripe</MenuItem>
                    <MenuItem value="APPLE">Apple App Store</MenuItem>
                    <MenuItem value="GOOGLE">Google Play Store</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={2} md={2}>
                <Button
                  fullWidth
                  type="submit"
                  variant="contained"
                  startIcon={<FilterIcon />}
                  sx={{
                    height: '56px',
                    borderRadius: '12px',
                    textTransform: 'none',
                    fontWeight: 700
                  }}
                >
                  Filter
                </Button>
              </Grid>

            </Grid>
          </form>
        </CardContent>
      </Card>

      {/* Datatable results */}
      <Card sx={{ borderRadius: '16px', overflow: 'hidden', mb: 4 }}>
        <TableContainer component={Paper} elevation={0} sx={{ backgroundColor: 'transparent' }}>
          <Table sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: theme.palette.surfaceContainerHigh }}>
                <TableCell sx={{ fontWeight: 700 }}>ORDER ID</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>ACCOUNT ID</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>PRODUCT SKU</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>PROVIDER</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>STATUS</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>TRANSACTION DATE</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {ordersLoading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                    <CircularProgress size={40} />
                  </TableCell>
                </TableRow>
              ) : orders.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                    <Typography variant="body1" sx={{ color: theme.palette.text.secondary }}>
                      🔍 No transactions match your current filter parameters.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                orders.map((order) => (
                  <TableRow
                    key={order.orderId}
                    hover
                    onClick={() => handleOrderClick(order)}
                    sx={{
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: theme.palette.surfaceContainerLow
                      },
                      transition: 'background-color 0.2s ease'
                    }}
                  >
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Tooltip title="Click to copy Order ID">
                        <Chip
                          label={order.orderId}
                          size="small"
                          onClick={() => copyToClipboard(order.orderId, "Order ID")}
                          onDelete={() => copyToClipboard(order.orderId, "Order ID")}
                          deleteIcon={<CopyIcon style={{ fontSize: '14px' }} />}
                          sx={{ fontFamily: 'monospace', cursor: 'pointer', borderRadius: '6px' }}
                        />
                      </Tooltip>
                    </TableCell>

                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Tooltip title="Click to copy Account ID">
                        <Chip
                          label={order.accountId}
                          size="small"
                          variant="outlined"
                          onClick={() => copyToClipboard(order.accountId, "Account ID")}
                          onDelete={() => copyToClipboard(order.accountId, "Account ID")}
                          deleteIcon={<CopyIcon style={{ fontSize: '14px' }} />}
                          sx={{ fontFamily: 'monospace', cursor: 'pointer', borderRadius: '6px' }}
                        />
                      </Tooltip>
                    </TableCell>

                    <TableCell sx={{ fontWeight: 600 }}>
                      {order.productId.replace(/_/g, ' ')}{isMocked && <span style={{ color: theme.palette.error.main, fontSize: '11px', marginLeft: '6px' }}>(Mocked)</span>}
                    </TableCell>

                    <TableCell>
                      <Chip
                        label={order.provider}
                        size="small"
                        sx={{ fontWeight: 700, fontSize: '11px', borderRadius: '8px', ...getProviderChipColor(order.provider) }}
                      />
                    </TableCell>

                    <TableCell>
                      <Chip
                        label={order.status}
                        size="small"
                        variant="outlined"
                        sx={{ fontWeight: 700, fontSize: '11px', borderRadius: '8px', ...getStatusChipColor(order.status) }}
                      />
                    </TableCell>

                    <TableCell sx={{ color: theme.palette.text.secondary }}>
                      {formatDate(order.purchaseDate)}
                    </TableCell>

                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ p: 2, display: 'flex', justifyContent: 'center', backgroundColor: theme.palette.surfaceContainerHigh }}>
          <Pagination
            count={Math.ceil(totalCount / size)}
            page={page}
            onChange={(e, val) => setPage(val)}
            color="primary"
          />
        </Box>
      </Card>

      {/* Product catalog card grid */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
          <CatalogIcon sx={{ color: theme.palette.primary.main }} />
          <Typography variant="h4" sx={{ fontWeight: 800 }}>
            Billing Products Catalog
          </Typography>
        </Box>

        {productsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={3}>
            {products.map((product) => (
              <Grid item xs={12} sm={6} md={3} key={product.productId}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', borderRadius: '16px' }}>
                  <CardContent sx={{ p: 3, flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Typography variant="h5" sx={{ fontWeight: 700 }}>
                        {product.name}
                      </Typography>
                      <Chip
                        label={product.status}
                        size="small"
                        color="success"
                        sx={{ fontWeight: 700, fontSize: '10px', borderRadius: '6px' }}
                      />
                    </Box>

                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary, flexGrow: 1 }}>
                      {product.description}
                    </Typography>

                    <Divider />

                    <List disablePadding sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <ListItem disablePadding sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="caption" sx={{ fontWeight: 800, color: theme.palette.text.secondary }}>OFFERING ID</Typography>
                        <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{product.offeringId}</Typography>
                      </ListItem>
                      <ListItem disablePadding sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="caption" sx={{ fontWeight: 800, color: theme.palette.text.secondary }}>TYPE</Typography>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>{product.offeringType}</Typography>
                      </ListItem>
                      <ListItem disablePadding sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="caption" sx={{ fontWeight: 800, color: theme.palette.text.secondary }}>STORE SKU</Typography>
                        <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{product.storeListingId}</Typography>
                      </ListItem>
                    </List>

                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>

      {/* Slide Drawer for transaction details */}
      <Drawer
        anchor="right"
        open={!!selectedOrder}
        onClose={() => { setSelectedOrder(null); setPayloadOpen(false); }}
        PaperProps={{
          sx: { width: { xs: '100%', sm: 480 }, borderRadius: '24px 0 0 24px', overflow: 'hidden' }
        }}
      >
        {selectedOrder && (
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            
            {/* Header */}
            <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 3,
              borderBottom: `1px solid ${theme.palette.divider}`,
              backgroundColor: theme.palette.surfaceContainerHigh
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <ReceiptIcon sx={{ color: theme.palette.primary.main }} />
                <Typography variant="h5" sx={{ fontWeight: 800 }}>
                  Transaction Receipt Details {isMocked && <span style={{ color: theme.palette.error.main, fontSize: '13px' }}>(Mocked)</span>}
                </Typography>
              </Box>
              <IconButton onClick={() => { setSelectedOrder(null); setPayloadOpen(false); }} size="medium">
                <CloseIcon />
              </IconButton>
            </Box>

            {/* Scrollable details */}
            <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 3, display: 'flex', flexDirection: 'column', gap: 3, '& > *': { flexShrink: 0 } }}>
              
              {/* Identity details */}
              <Card sx={{ borderRadius: '16px', backgroundColor: theme.palette.surfaceContainerLow }}>
                <CardContent sx={{ p: 2.5 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 2, color: theme.palette.text.secondary, textTransform: 'uppercase' }}>
                    Billing Core
                  </Typography>
                  <List disablePadding sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    
                    <ListItem disablePadding sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <ListItemText
                        primary="ORDER ID"
                        secondary={selectedOrder.orderId}
                        primaryTypographyProps={{ variant: 'caption', sx: { fontWeight: 800, color: theme.palette.text.secondary } }}
                        secondaryTypographyProps={{ variant: 'body2', sx: { fontFamily: 'monospace', fontWeight: 600, color: theme.palette.text.primary, mt: 0.5 } }}
                      />
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<CopyIcon style={{ fontSize: '12px' }} />}
                        onClick={() => copyToClipboard(selectedOrder.orderId, "Order ID")}
                        sx={{ borderRadius: '8px', textTransform: 'none', py: 0.5 }}
                      >
                        Copy
                      </Button>
                    </ListItem>

                    <ListItem disablePadding sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <ListItemText
                        primary="RECEIPT TRANSACTION ID"
                        secondary={receiptLoading ? <CircularProgress size={12} /> : (selectedOrder.purchaseReceiptId || '-')}
                        primaryTypographyProps={{ variant: 'caption', sx: { fontWeight: 800, color: theme.palette.text.secondary } }}
                        secondaryTypographyProps={{ variant: 'body2', sx: { fontFamily: 'monospace', fontWeight: 600, color: theme.palette.text.primary, mt: 0.5 } }}
                      />
                      {!receiptLoading && selectedOrder.purchaseReceiptId && (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<CopyIcon style={{ fontSize: '12px' }} />}
                          onClick={() => copyToClipboard(selectedOrder.purchaseReceiptId, "Receipt ID")}
                          sx={{ borderRadius: '8px', textTransform: 'none', py: 0.5 }}
                        >
                          Copy
                        </Button>
                      )}
                    </ListItem>

                    <ListItem disablePadding sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <ListItemText
                        primary="ACCOUNT OWNER ID"
                        secondary={selectedOrder.accountId}
                        primaryTypographyProps={{ variant: 'caption', sx: { fontWeight: 800, color: theme.palette.text.secondary } }}
                        secondaryTypographyProps={{ variant: 'body2', sx: { fontFamily: 'monospace', fontWeight: 600, color: theme.palette.text.primary, mt: 0.5 } }}
                      />
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<CopyIcon style={{ fontSize: '12px' }} />}
                        onClick={() => copyToClipboard(selectedOrder.accountId, "Account ID")}
                        sx={{ borderRadius: '8px', textTransform: 'none', py: 0.5 }}
                      >
                        Copy
                      </Button>
                    </ListItem>

                    {ownerLoading ? (
                      <ListItem disablePadding sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                        <CircularProgress size={20} />
                      </ListItem>
                    ) : ownerDetails ? (
                      <>
                        <ListItem disablePadding sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <ListItemText
                            primary="OWNER DISPLAY NAME"
                            secondary={ownerDetails.name + (ownerDetails.isMocked ? " (Mocked)" : "")}
                            primaryTypographyProps={{ variant: 'caption', sx: { fontWeight: 800, color: theme.palette.text.secondary } }}
                            secondaryTypographyProps={{ variant: 'body2', sx: { fontWeight: 700, color: theme.palette.text.primary, mt: 0.5 } }}
                          />
                        </ListItem>
                        <ListItem disablePadding sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <ListItemText
                            primary="OWNER EMAIL ADDRESS"
                            secondary={ownerDetails.email}
                            primaryTypographyProps={{ variant: 'caption', sx: { fontWeight: 800, color: theme.palette.text.secondary } }}
                            secondaryTypographyProps={{ variant: 'body2', sx: { fontWeight: 600, color: theme.palette.text.primary, mt: 0.5 } }}
                          />
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<CopyIcon style={{ fontSize: '12px' }} />}
                            onClick={() => copyToClipboard(ownerDetails.email, "Owner Email")}
                            sx={{ borderRadius: '8px', textTransform: 'none', py: 0.5 }}
                          >
                            Copy
                          </Button>
                        </ListItem>
                      </>
                    ) : null}

                    <ListItem disablePadding sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" sx={{ fontWeight: 800, color: theme.palette.text.secondary }}>PAYMENT PROVIDER</Typography>
                      <Chip label={selectedOrder.provider} size="small" sx={{ fontWeight: 700, fontSize: '11px', borderRadius: '8px', ...getProviderChipColor(selectedOrder.provider) }} />
                    </ListItem>

                    <ListItem disablePadding sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" sx={{ fontWeight: 800, color: theme.palette.text.secondary }}>PAYMENT STATUS</Typography>
                      <Chip label={selectedOrder.status} size="small" variant="outlined" sx={{ fontWeight: 700, fontSize: '11px', borderRadius: '8px', ...getStatusChipColor(selectedOrder.status) }} />
                    </ListItem>

                    <ListItem disablePadding sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" sx={{ fontWeight: 800, color: theme.palette.text.secondary }}>TRANSACTION VALUE</Typography>
                      {receiptLoading ? <CircularProgress size={12} /> : (
                        <Typography variant="body2" sx={{ fontWeight: 700, color: theme.palette.success.main }}>
                          {formatCurrency(selectedOrder.amount || getProductPrice(selectedOrder.productId))}
                        </Typography>
                      )}
                    </ListItem>

                    <ListItem disablePadding sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" sx={{ fontWeight: 800, color: theme.palette.text.secondary }}>RECEIPT VERIFICATION</Typography>
                      {receiptLoading ? <CircularProgress size={12} /> : (
                        <Chip
                          label={selectedOrder.isVerified ? "VERIFIED" : "UNVERIFIED"}
                          size="small"
                          sx={{
                            fontWeight: 700,
                            fontSize: '10px',
                            borderRadius: '6px',
                            backgroundColor: selectedOrder.isVerified ? 'rgba(76, 175, 80, 0.15)' : 'rgba(244, 67, 54, 0.15)',
                            color: selectedOrder.isVerified ? theme.palette.success.main : theme.palette.error.main
                          }}
                        />
                      )}
                    </ListItem>

                  </List>
                </CardContent>
              </Card>

              {/* Transaction specifics */}
              <Card sx={{ borderRadius: '16px', backgroundColor: theme.palette.surfaceContainerLow }}>
                <CardContent sx={{ p: 2.5 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 2, color: theme.palette.text.secondary, textTransform: 'uppercase' }}>
                    Transaction Registry Audit
                  </Typography>
                  <List disablePadding sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    
                    <ListItem disablePadding sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <ListItemText
                        primary="STORE PRODUCT SKU"
                        secondary={selectedOrder.productId}
                        primaryTypographyProps={{ variant: 'caption', sx: { fontWeight: 800, color: theme.palette.text.secondary } }}
                        secondaryTypographyProps={{ variant: 'body2', sx: { fontWeight: 700, color: theme.palette.primary.main, mt: 0.5 } }}
                      />
                    </ListItem>

                    <ListItem disablePadding sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <ListItemText
                        primary="BILLING ENVIRONMENT"
                        secondary={selectedOrder.environment}
                        primaryTypographyProps={{ variant: 'caption', sx: { fontWeight: 800, color: theme.palette.text.secondary } }}
                        secondaryTypographyProps={{ variant: 'body2', sx: { fontWeight: 800, color: selectedOrder.environment === 'PRODUCTION' ? theme.palette.success.main : theme.palette.warning.main, mt: 0.5 } }}
                      />
                    </ListItem>

                    <ListItem disablePadding sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <ListItemText
                        primary="PURCHASE TIMEsTAMP"
                        secondary={formatDate(selectedOrder.purchaseDate)}
                        primaryTypographyProps={{ variant: 'caption', sx: { fontWeight: 800, color: theme.palette.text.secondary } }}
                        secondaryTypographyProps={{ variant: 'body2', sx: { fontWeight: 600, color: theme.palette.text.primary, mt: 0.5 } }}
                      />
                    </ListItem>

                    <ListItem disablePadding sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <ListItemText
                        primary="REGISTRATION TIMESTAMP"
                        secondary={formatDate(selectedOrder.createdAt)}
                        primaryTypographyProps={{ variant: 'caption', sx: { fontWeight: 800, color: theme.palette.text.secondary } }}
                        secondaryTypographyProps={{ variant: 'body2', sx: { fontWeight: 600, color: theme.palette.text.primary, mt: 0.5 } }}
                      />
                    </ListItem>

                    {!receiptLoading && selectedOrder.formattedBillingAddress && (
                      <ListItem disablePadding sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <ListItemText
                          primary="BILLING ADDRESS COUNTRY"
                          secondary={selectedOrder.formattedBillingAddress}
                          primaryTypographyProps={{ variant: 'caption', sx: { fontWeight: 800, color: theme.palette.text.secondary } }}
                          secondaryTypographyProps={{ variant: 'body2', sx: { fontWeight: 600, color: theme.palette.text.primary, mt: 0.5 } }}
                        />
                      </ListItem>
                    )}

                  </List>
                </CardContent>
              </Card>

              {/* Collapsible raw webhook payload card */}
              <Card sx={{ borderRadius: '16px', backgroundColor: theme.palette.surfaceContainerLow }}>
                <CardContent sx={{ p: 2.5 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800, color: theme.palette.text.secondary, textTransform: 'uppercase' }}>
                      Raw webhook payload
                    </Typography>
                    <IconButton size="small" onClick={() => setPayloadOpen(!payloadOpen)} color="primary">
                      <CodeIcon />
                    </IconButton>
                  </Box>

                  {payloadOpen && (
                    <Box sx={{ mt: 2 }}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2,
                          backgroundColor: '#0f172a',
                          color: '#38bdf8',
                          borderRadius: '8px',
                          overflowX: 'auto',
                          maxHeight: 250,
                          overflowY: 'auto'
                        }}
                      >
                        <pre style={{ margin: 0, fontFamily: 'monospace', fontSize: '11px', whiteSpace: 'pre-wrap' }}>
                          {selectedOrder.payload ? JSON.stringify(JSON.parse(selectedOrder.payload), null, 2) : "No raw webhook payload registered."}
                        </pre>
                      </Paper>
                    </Box>
                  )}
                </CardContent>
              </Card>

            </Box>

          </Box>
        )}
      </Drawer>

    </Box>
  );
};

export default MonetizationDashboardTab;
